/**
 * Base Client
 * Core fetch logic, authentication headers, and error handling
 */

import { logger } from '../logger';
import type { OperatorRole, OperatorSession } from './types';
import { API_URL } from './constants';

// =============================================================================
// Session Management
// =============================================================================

/**
 * Retrieves the operator ID from browser session storage.
 *
 * @description Safely retrieves the authenticated operator's ID from session storage.
 * Returns null when called server-side (no window object) or when not authenticated.
 *
 * @returns The operator ID string if authenticated, or null otherwise
 *
 * @example
 * ```typescript
 * const operatorId = getOperatorId();
 * if (operatorId) {
 *   console.log(`Current operator: ${operatorId}`);
 * } else {
 *   // Redirect to login
 * }
 * ```
 */
export function getOperatorId(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem('operatorId');
}

/**
 * Retrieves the complete operator session data from browser session storage.
 *
 * @description Reconstructs the full operator session including ID, role, name,
 * and assigned business IDs from individual session storage items. Handles
 * server-side rendering gracefully by returning null.
 *
 * @returns Complete OperatorSession object if authenticated, or null if:
 *   - Called server-side (no window object)
 *   - Required session data (id, role, name) is missing
 *   - Session has expired or been cleared
 *
 * @example
 * ```typescript
 * const operator = getCurrentOperator();
 * if (operator) {
 *   console.log(`${operator.name} (${operator.role})`);
 *   console.log(`Access to ${operator.businessIds.length} businesses`);
 * }
 * ```
 */
export function getCurrentOperator(): OperatorSession | null {
  if (typeof window === 'undefined') return null;

  const id = sessionStorage.getItem('operatorId');
  const roleRaw = sessionStorage.getItem('operatorRole');
  const name = sessionStorage.getItem('operatorName');
  const businessIdsJson = sessionStorage.getItem('operatorBusinessIds');

  // Validate required fields - if any are missing or 'undefined' string, clear session
  if (!id || !roleRaw || !name || roleRaw === 'undefined' || name === 'undefined') {
    // Clear corrupted session data
    clearOperatorSession();
    return null;
  }

  // Validate role is a known role
  const validRoles = ['ADMIN', 'MANAGER', 'SUPERVISOR', 'SUPPORT', 'VIEWER', 'COMPLIANCE'];
  if (!validRoles.includes(roleRaw)) {
    clearOperatorSession();
    return null;
  }

  const role = roleRaw as OperatorRole;

  let businessIds: string[] = [];
  if (businessIdsJson) {
    try {
      businessIds = JSON.parse(businessIdsJson);
    } catch {
      businessIds = [];
    }
  }

  return { id, role, name, businessIds };
}

/**
 * Stores operator session data in browser session storage.
 *
 * @description Persists all operator session data including authentication
 * credentials and authorization scope. Data is stored in session storage
 * (cleared when browser tab closes) for security.
 *
 * @param operatorId - Unique identifier for the operator
 * @param role - Operator role (ADMIN, MANAGER, SUPPORT, VIEWER)
 * @param name - Display name for UI rendering
 * @param businessIds - Array of business IDs the operator can access
 *
 * @example
 * ```typescript
 * // After successful authentication
 * storeOperatorSession(
 *   'op-123',
 *   'MANAGER',
 *   'John Smith',
 *   ['biz-001', 'biz-002']
 * );
 * ```
 */
export function storeOperatorSession(
  operatorId: string,
  role: OperatorRole,
  name: string,
  businessIds: string[]
): void {
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('operatorId', operatorId);
    sessionStorage.setItem('operatorRole', role);
    sessionStorage.setItem('operatorName', name);
    sessionStorage.setItem('operatorBusinessIds', JSON.stringify(businessIds));
  }
}

/**
 * Clears all operator session data from browser session storage.
 *
 * @description Removes all authentication and authorization data, effectively
 * logging out the operator. Also clears any selected business context.
 * Safe to call server-side (no-op).
 *
 * @example
 * ```typescript
 * // Handle logout
 * clearOperatorSession();
 * router.push('/login');
 * ```
 */
export function clearOperatorSession(): void {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('operatorId');
    sessionStorage.removeItem('operatorRole');
    sessionStorage.removeItem('operatorName');
    sessionStorage.removeItem('operatorBusinessIds');
    sessionStorage.removeItem('selectedBusinessId');
  }
}

// =============================================================================
// HTTP Client Utilities
// =============================================================================

/**
 * Gets the JWT auth token from localStorage (set by AuthProvider).
 */
function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem('auth_token');
  } catch {
    return null;
  }
}

/**
 * Authenticated fetch wrapper with structured logging and 401 handling.
 *
 * Replaces raw `fetch()` + `createHeaders()` across all operator pages.
 * - Logs every request: method, URL, status, duration
 * - On 401: clears auth state and redirects to /sign-in
 * - On non-2xx: logs as error with response body
 *
 * @param url - API endpoint path (e.g., '/api/operator/bookings?businessId=...')
 * @param options - Standard RequestInit options (method, body, etc.)
 * @returns The fetch Response object
 * @throws Error on network failure or 401 (after redirect)
 */
export async function apiFetch(url: string, options?: RequestInit): Promise<Response> {
  const start = Date.now();
  const method = options?.method || 'GET';

  // Merge auth headers with any caller-provided headers
  const authHeaders = createHeaders();
  const mergedHeaders: Record<string, string> = { ...(authHeaders as Record<string, string>) };
  if (options?.headers) {
    const extra = options.headers as Record<string, string>;
    Object.assign(mergedHeaders, extra);
  }

  try {
    const response = await fetch(url, { ...options, headers: mergedHeaders });
    const duration = Date.now() - start;

    if (response.status === 401) {
      console.error(`[API] ${method} ${url} → 401 UNAUTHORIZED (${duration}ms) — session expired`);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      sessionStorage.clear();
      window.location.href = '/sign-in';
      throw new Error('Session expired. Redirecting to login.');
    }

    // 403 "Access denied to this business" means the JWT's businessIds
    // don't match the selected business — stale token after DB re-seed.
    // Clear state and re-login to get a fresh JWT with current business IDs.
    if (response.status === 403) {
      const body = await response.clone().json().catch(() => null);
      if (body?.error === 'Access denied to this business') {
        console.error(`[API] ${method} ${url} → 403 BUSINESS ACCESS DENIED (${duration}ms) — stale JWT, re-authenticating`);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        sessionStorage.clear();
        window.location.href = '/api/auth/dev/login';
        throw new Error('Business access denied. Re-authenticating with fresh token.');
      }
    }

    if (!response.ok) {
      console.error(`[API] ${method} ${url} → ${response.status} (${duration}ms)`);
    }

    return response;
  } catch (error) {
    const duration = Date.now() - start;
    // Don't double-log redirect errors (401 or 403 business access)
    if (error instanceof Error && (error.message.includes('Session expired') || error.message.includes('Re-authenticating'))) {
      throw error;
    }
    console.error(`[API] ${method} ${url} → NETWORK ERROR (${duration}ms):`, error);
    throw error;
  }
}

/**
 * Creates HTTP headers with operator authentication for API requests.
 *
 * @description Builds headers including Content-Type, Authorization Bearer token
 * (for JWT auth), and X-Operator-ID header. Used for standard API requests.
 *
 * @returns HeadersInit object with Content-Type, Authorization, and optional X-Operator-ID
 */
export function createHeaders(): HeadersInit {
  const operatorId = getOperatorId();
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (operatorId) {
    headers['X-Operator-ID'] = operatorId;
  }
  return headers;
}

/**
 * Creates HTTP headers with full operator context for role-based API requests.
 *
 * @description Builds headers including Content-Type, Authorization Bearer token,
 * X-Operator-ID, and X-Operator-Role for RBAC endpoints.
 *
 * @returns HeadersInit object with full authentication context
 */
export function createFullHeaders(): HeadersInit {
  const operator = getCurrentOperator();
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (operator?.id) {
    headers['X-Operator-ID'] = operator.id;
  }
  if (operator?.role) {
    headers['X-Operator-Role'] = operator.role;
  }
  return headers;
}

// =============================================================================
// API Request Helpers
// =============================================================================

export interface ApiRequestOptions {
  /** HTTP method */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  /** Request body (will be JSON stringified) */
  body?: unknown;
  /** URL parameters */
  params?: Record<string, string | number | undefined>;
  /** Include role in headers */
  includeRole?: boolean;
}

/**
 * Builds a complete URL with query parameters from an endpoint and params object.
 *
 * @description Constructs a full API URL by combining the base API URL with an
 * endpoint path and optional query parameters. Automatically filters out null,
 * undefined, and empty string values from parameters.
 *
 * @param endpoint - API endpoint path (e.g., '/conversations')
 * @param params - Optional key-value pairs for query string parameters
 *
 * @returns Complete URL string with encoded query parameters
 *
 * @example
 * ```typescript
 * // Simple endpoint
 * buildUrl('/users');
 * // 'https://api.example.com/users'
 *
 * // With parameters
 * buildUrl('/bookings', { status: 'CONFIRMED', page: 1, limit: 20 });
 * // 'https://api.example.com/bookings?status=CONFIRMED&page=1&limit=20'
 *
 * // Undefined values are filtered out
 * buildUrl('/search', { q: 'test', filter: undefined });
 * // 'https://api.example.com/search?q=test'
 * ```
 */
export function buildUrl(endpoint: string, params?: Record<string, string | number | undefined>): string {
  const url = `${API_URL}${endpoint}`;
  if (!params) return url;

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${url}?${queryString}` : url;
}

/**
 * Makes an authenticated API request with automatic logging and error handling.
 *
 * @description A generic API client function that handles authentication headers,
 * request body serialization, timing/logging, and error wrapping. Provides a
 * consistent interface for all API operations.
 *
 * @typeParam T - The expected response type
 *
 * @param endpoint - API endpoint path (e.g., '/conversations/123')
 * @param options - Request configuration options
 * @param options.method - HTTP method (defaults to 'GET')
 * @param options.body - Request body (automatically JSON stringified)
 * @param options.params - URL query parameters
 * @param options.includeRole - Include X-Operator-Role header for RBAC (default: false)
 *
 * @returns Promise resolving to the typed response data
 *
 * @throws {ApiError} When the response status is not OK (4xx, 5xx)
 *
 * @example
 * ```typescript
 * // GET request
 * const conversations = await apiRequest<ConversationListResponse>('/conversations');
 *
 * // POST request with body
 * const booking = await apiRequest<Booking>('/bookings', {
 *   method: 'POST',
 *   body: { customerId: 'cust-123', serviceId: 'svc-456' }
 * });
 *
 * // With query params and role-based auth
 * const data = await apiRequest<AdminData>('/admin/reports', {
 *   params: { startDate: '2024-01-01', endDate: '2024-01-31' },
 *   includeRole: true
 * });
 * ```
 */
export async function apiRequest<T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const { method = 'GET', body, params, includeRole = false } = options;
  const startTime = Date.now();
  const url = buildUrl(endpoint, params);
  const headers = includeRole ? createFullHeaders() : createHeaders();

  const fetchOptions: RequestInit = {
    method,
    headers,
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);
  const duration = Date.now() - startTime;

  logger.apiCall(endpoint, method, duration, response.status);

  if (!response.ok) {
    throw new ApiError(
      `API error: ${response.status}`,
      response.status,
      endpoint,
      method
    );
  }

  return response.json();
}

// =============================================================================
// Error Handling
// =============================================================================

/** Custom error class for API errors */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly endpoint: string,
    public readonly method: string
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Check if error is due to access denied (403) */
  get isAccessDenied(): boolean {
    return this.status === 403;
  }

  /** Check if error is due to not found (404) */
  get isNotFound(): boolean {
    return this.status === 404;
  }
}

/**
 * Handle API errors with logging
 * @param error - Error to handle
 * @param component - Component name for logging
 * @param context - Additional context for logging
 */
export function handleApiError(
  error: unknown,
  component: string,
  context?: Record<string, unknown>
): void {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;

  logger.error(`Error in ${component}`, {
    component: 'operator-api-client',
    ...context,
    error: errorMessage,
    stack,
  });
}

/**
 * Log API call with optional mock indicator
 * @param endpoint - API endpoint
 * @param method - HTTP method
 * @param duration - Request duration in ms
 * @param status - HTTP status code
 * @param context - Additional context
 */
export function logApiCall(
  endpoint: string,
  method: string,
  duration: number,
  status: number,
  context?: Record<string, unknown>
): void {
  logger.apiCall(endpoint, method, duration, status, context);
}
